/**
 * Knowledge Brain API client — Patch 27.
 *
 * Every request is workspace-scoped via the http() interceptor's
 * X-Workspace-Id header. Nothing here talks to a provider directly —
 * the Knowledge Brain is a pure data-and-approval surface.
 */
import { http } from "@/shared/api/http";


export type KnowledgeSourceKind = "upload" | "website" | "google_drive" | "onedrive" | "dropbox" | "notion" | "hubspot" | "zendesk" | "custom";
export type KnowledgeSourceStatus = "discovered" | "parsing" | "parsed" | "proposed" | "awaiting_approval" | "approved" | "rejected" | "archived" | "failed";
export type KnowledgeEntryKind = "company_fact" | "positioning" | "product" | "pricing" | "offer" | "proof_point" | "faq" | "process" | "story" | "tone_exemplar" | "compliance" | "other";
export type KnowledgeChunkStatus = "proposed" | "awaiting_approval" | "approved" | "rejected" | "superseded";


export interface KnowledgeSource {
  _id?: string;
  source_id?: string;
  workspace_id: string;
  kind: KnowledgeSourceKind;
  filename?: string | null;
  mime_type?: string | null;
  size_bytes?: number | null;
  url?: string | null;
  page_title?: string | null;
  status: KnowledgeSourceStatus;
  status_reason?: string | null;
  chunks_produced: number;
  chunks_approved: number;
  chunks_rejected: number;
  created_at: string;
  created_by?: string | null;
}


export interface KnowledgeChunk {
  _id: string;
  workspace_id: string;
  source_id: string;
  raw_text: string;
  proposed_kind: KnowledgeEntryKind;
  proposed_summary: string;
  proposed_key_facts: string[];
  proposed_tags: string[];
  parser_confidence: number;
  parser_rationale: string;
  status: KnowledgeChunkStatus;
  section_hint?: string | null;
  created_at: string;
}


export interface KnowledgeEntry {
  entry_id: string;
  workspace_id: string;
  source_id: string;
  chunk_id: string;
  kind: KnowledgeEntryKind;
  summary: string;
  body_markdown: string;
  key_facts: string[];
  tags: string[];
  relevance_score: number;
  is_active: boolean;
  is_edited: boolean;
  created_at: string;
  approved_by?: string | null;
}


export interface KnowledgeSummary {
  workspace_id: string;
  sources_total: number;
  chunks_awaiting_approval: number;
  entries_active: number;
  entries_by_kind: Record<string, number>;
}


export async function getSummary(ws: string): Promise<KnowledgeSummary> {
  return http<KnowledgeSummary>(`/workspaces/${ws}/knowledge/summary`);
}

export async function listSources(ws: string): Promise<KnowledgeSource[]> {
  return http<KnowledgeSource[]>(`/workspaces/${ws}/knowledge/sources`);
}

export async function listQueue(ws: string): Promise<KnowledgeChunk[]> {
  return http<KnowledgeChunk[]>(`/workspaces/${ws}/knowledge/queue`);
}

export async function listEntries(ws: string): Promise<KnowledgeEntry[]> {
  return http<KnowledgeEntry[]>(`/workspaces/${ws}/knowledge/entries`);
}

export async function approveChunk(
  ws: string,
  chunkId: string,
  overrides?: Partial<{
    kind: string;
    summary: string;
    body_markdown: string;
    key_facts: string[];
    tags: string[];
    relevance_score: number;
  }>,
): Promise<KnowledgeEntry> {
  return http<KnowledgeEntry>(`/workspaces/${ws}/knowledge/chunks/${chunkId}/approve`, {
    method: "POST",
    body: overrides ?? {},
  });
}

export async function rejectChunk(ws: string, chunkId: string, reason?: string): Promise<void> {
  await http(`/workspaces/${ws}/knowledge/chunks/${chunkId}/reject`, {
    method: "POST",
    body: { reason: reason ?? null },
  });
}

export async function ingestUrl(ws: string, url: string): Promise<{ source: KnowledgeSource; chunks_produced: number }> {
  return http(`/workspaces/${ws}/knowledge/ingest-url`, {
    method: "POST",
    body: { url },
  });
}

export async function uploadDocument(ws: string, file: File): Promise<{ source: KnowledgeSource; chunks_produced: number }> {
  const form = new FormData();
  form.append("file", file);
  return http(`/workspaces/${ws}/knowledge/upload`, {
    method: "POST",
    body: form,
  });
}


export const KIND_LABEL: Record<KnowledgeEntryKind, string> = {
  company_fact: "Company Fact",
  positioning: "Positioning",
  product: "Product",
  pricing: "Pricing",
  offer: "Offer",
  proof_point: "Proof Point",
  faq: "FAQ",
  process: "Process",
  story: "Story",
  tone_exemplar: "Tone Exemplar",
  compliance: "Compliance",
  other: "Other",
};
